import React, { useState, useEffect, useRef } from 'react';
import {
    FiUpload,
    FiImage,
    FiTrash2,
    FiSave,
    FiLoader,
    FiToggleLeft,
    FiToggleRight,
    FiEdit,
    FiCheckCircle,
    FiAlertCircle,
    FiHelpCircle,
    FiX
} from 'react-icons/fi';

import {
    storage,
    database,
    ref as dbRef,
    set,
    get,
    uploadBytes,
    getDownloadURL,
    storageRef,
    deleteObject
} from '../../firebase';

import AdminNavbar from './AdminNavbar';
import AdminSidebar from './AdminSidebar';

const AdminPopup = () => {
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [popupData, setPopupData] = useState({
        imageUrl: '',
        isVisible: true,
        storagePath: ''
    });
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [uploading, setUploading] = useState(false);

    const [loading, setLoading] = useState(true);
    const [statusModal, setStatusModal] = useState({ show: false, type: '', message: '' });
    const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });
    const fileInputRef = useRef(null);

    const toggleSidebar = () => setSidebarCollapsed(prev => !prev);

    useEffect(() => {
        loadPopupData();
    }, []);

    const loadPopupData = async () => {
        try {
            setLoading(true);
            const popupRef = dbRef(database, 'shivpratapmultistate/popup');
            const snapshot = await get(popupRef);

            if (snapshot.exists()) {
                const data = snapshot.val();
                setPopupData({
                    imageUrl: data.imageUrl || '',
                    isVisible: data.isVisible !== undefined ? data.isVisible : true,
                    storagePath: data.storagePath || ''
                });
                if (data.imageUrl) {
                    setPreviewUrl(data.imageUrl);
                }
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setStatusModal({ show: true, type: 'error', message: 'Please select an image file' });
            return;
        }

        setSelectedFile(file);
        const reader = new FileReader();
        reader.onload = (e) => setPreviewUrl(e.target.result);
        reader.readAsDataURL(file);
    };

    const handleSave = async () => {
        setUploading(true);
        try {
            let imageUrl = popupData.imageUrl;
            let storagePath = popupData.storagePath;

            // If a new file is selected, upload it
            if (selectedFile) {
                // Create a reference for the new image
                const filename = `popup_${Date.now()}_${selectedFile.name}`;
                const imgRef = storageRef(storage, `shivpratapmultistate/popup/${filename}`);

                // Upload the file
                const snap = await uploadBytes(imgRef, selectedFile);
                imageUrl = await getDownloadURL(snap.ref);
                storagePath = `shivpratapmultistate/popup/${filename}`;

                // Optional: Delete old image if it exists to save space
                if (popupData.storagePath) {
                    try {
                        const oldRef = storageRef(storage, popupData.storagePath);
                        await deleteObject(oldRef);
                    } catch (error) {
                        console.log("Could not delete old image, might not exist", error);
                    }
                }
            }

            // Update Database
            const popupDbRef = dbRef(database, 'shivpratapmultistate/popup');
            await set(popupDbRef, {
                imageUrl,
                isVisible: popupData.isVisible,
                storagePath,
                updatedAt: new Date().toISOString()
            });

            setPopupData(prev => ({ ...prev, imageUrl, storagePath }));
            setSelectedFile(null);
            setStatusModal({ show: true, type: 'success', message: 'Popup settings saved successfully!' });
        } catch (err) {
            console.error(err);
            setStatusModal({ show: true, type: 'error', message: 'Failed to save popup settings' });
        } finally {
            setUploading(false);
        }
    };

    const toggleVisibility = async () => {
        const newVisibility = !popupData.isVisible;
        setPopupData(prev => ({ ...prev, isVisible: newVisibility }));

        // Auto save visibility change
        try {
            const popupDbRef = dbRef(database, 'shivpratapmultistate/popup');
            await set(popupDbRef, {
                ...popupData,
                isVisible: newVisibility,
                updatedAt: new Date().toISOString()
            });
        } catch (err) {
            console.error("Error updating visibility", err);
            // Revert if failed
            setPopupData(prev => ({ ...prev, isVisible: !newVisibility }));
            setStatusModal({ show: true, type: 'error', message: 'Failed to update visibility' });
        }
    };

    const handleDeleteImage = () => {
        setConfirmModal({
            show: true,
            message: "Are you sure you want to remove the popup image?",
            onConfirm: performDelete
        });
    };

    const performDelete = async () => {
        setConfirmModal({ show: false, message: '', onConfirm: null });
        setUploading(true);
        try {
            if (popupData.storagePath) {
                const oldRef = storageRef(storage, popupData.storagePath);
                await deleteObject(oldRef);
            }

            const popupDbRef = dbRef(database, 'shivpratapmultistate/popup');
            await set(popupDbRef, {
                imageUrl: '',
                isVisible: false,
                storagePath: '',
                updatedAt: new Date().toISOString()
            });

            setPopupData({
                imageUrl: '',
                isVisible: false,
                storagePath: ''
            });
            setPreviewUrl('');
            setSelectedFile(null);
            setStatusModal({ show: true, type: 'success', message: 'Popup image removed successfully' });
        } catch (err) {
            console.error(err);
            setStatusModal({ show: true, type: 'error', message: 'Failed to remove image' });
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 overflow-x-hidden">
            <AdminSidebar collapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} />
            <AdminNavbar toggleSidebar={toggleSidebar} title="Popup Management" />

            {/* Responsive Content Wrapper */}
            <div
                className={`pt-20 transition-all duration-300 
          ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'} 
          ml-0`}
            >
                <div className="max-w-7xl mx-auto p-4 sm:p-6">
                    {/* Header */}
                    <div className="bg-white rounded-xl shadow p-4 sm:p-6 mb-6">
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Popup Management</h1>
                        <p className="text-gray-600 text-sm sm:text-base mt-2">
                            Manage the promotional popup image shown on the website homepage.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Settings Card */}
                        <div className="bg-white rounded-xl shadow p-6">
                            <h2 className="text-xl font-bold mb-4 flex items-center">
                                <FiToggleLeft className="mr-2 text-blue-600" />
                                Visibility Settings
                            </h2>

                            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg mb-6">
                                <div>
                                    <h3 className="font-semibold text-gray-800">Show Popup on Homepage</h3>
                                    <p className="text-sm text-gray-500">Enable or disable the popup</p>
                                </div>
                                <button
                                    onClick={toggleVisibility}
                                    className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none ${popupData.isVisible ? 'bg-green-500' : 'bg-gray-300'}`}
                                >
                                    <span
                                        className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${popupData.isVisible ? 'translate-x-7' : 'translate-x-1'}`}
                                    />
                                </button>
                            </div>

                            <h2 className="text-xl font-bold mb-4 flex items-center mt-8">
                                <FiUpload className="mr-2 text-blue-600" />
                                Update Image
                            </h2>

                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:bg-gray-50 transition-colors relative">
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    accept="image/*"
                                    onChange={handleFileSelect}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                />
                                <div className="space-y-2">
                                    <FiImage className="mx-auto text-4xl text-gray-400" />
                                    <p className="text-gray-600">Click to upload a new image</p>
                                    <p className="text-xs text-gray-400">Recommended size: 800x600px (JPG, PNG)</p>
                                </div>
                            </div>

                            <div className="mt-6 flex justify-end">
                                <button
                                    onClick={handleSave}
                                    disabled={uploading || (!selectedFile && !popupData.imageUrl)}
                                    className={`flex items-center px-6 py-2.5 rounded-lg text-white font-medium transition-all
                              ${uploading
                                            ? 'bg-blue-400 cursor-not-allowed'
                                            : 'bg-blue-600 hover:bg-blue-700 shadow-lg hover:shadow-xl'
                                        }`}
                                >
                                    {uploading ? (
                                        <>
                                            <FiLoader className="animate-spin mr-2" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <FiSave className="mr-2" />
                                            Save Changes
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Preview Card */}
                        <div className="bg-white rounded-xl shadow p-6">
                            <h2 className="text-xl font-bold mb-4 text-gray-800">Preview</h2>

                            <div className="bg-gray-100 rounded-lg min-h-[300px] flex items-center justify-center relative overflow-hidden">
                                {loading ? (
                                    <FiLoader className="animate-spin text-4xl text-blue-500" />
                                ) : previewUrl ? (
                                    <div className="relative w-full h-full group">
                                        <img
                                            src={previewUrl}
                                            alt="Popup Preview"
                                            className="w-full h-auto object-contain max-h-[400px]"
                                        />
                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                                            <button
                                                onClick={() => fileInputRef.current?.click()}
                                                className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center hover:bg-blue-600 transition-colors"
                                            >
                                                <FiEdit className="mr-2" /> Edit
                                            </button>
                                            <button
                                                onClick={handleDeleteImage}
                                                className="bg-red-500 text-white px-4 py-2 rounded-lg flex items-center hover:bg-red-600 transition-colors"
                                            >
                                                <FiTrash2 className="mr-2" /> Remove
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center text-gray-400">
                                        <FiImage className="mx-auto text-4xl mb-2" />
                                        <p>No image uploaded</p>
                                    </div>
                                )}
                            </div>

                            <div className="mt-4 p-4 bg-yellow-50 text-yellow-800 rounded-lg text-sm border border-yellow-200">
                                <strong>Note:</strong> Changes to the popup image will reflect immediately on the homepage for all users.
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Centered Status Modal */}
            {statusModal.show && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm transform transition-all scale-100 opacity-100">
                        <div className="flex flex-col items-center text-center">
                            <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${statusModal.type === 'success' ? 'bg-green-100 text-green-500' : 'bg-red-100 text-red-500'
                                }`}>
                                {statusModal.type === 'success' ? <FiCheckCircle size={32} /> : <FiAlertCircle size={32} />}
                            </div>

                            <h3 className={`text-xl font-bold mb-2 ${statusModal.type === 'success' ? 'text-gray-800' : 'text-red-600'
                                }`}>
                                {statusModal.type === 'success' ? 'Success!' : 'Error'}
                            </h3>

                            <p className="text-gray-600 mb-6">
                                {statusModal.message}
                            </p>

                            <button
                                onClick={() => setStatusModal({ ...statusModal, show: false })}
                                className={`px-6 py-2.5 rounded-xl text-white font-medium w-full transition-colors ${statusModal.type === 'success'
                                    ? 'bg-green-500 hover:bg-green-600 shadow-lg shadow-green-500/30'
                                    : 'bg-red-500 hover:bg-red-600 shadow-lg shadow-red-500/30'
                                    }`}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Modal */}
            {confirmModal.show && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm">
                        <div className="flex flex-col items-center text-center">
                            <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4 bg-blue-100 text-blue-500">
                                <FiHelpCircle size={32} />
                            </div>

                            <h3 className="text-xl font-bold mb-2 text-gray-800">
                                Confirmation
                            </h3>

                            <p className="text-gray-600 mb-6">
                                {confirmModal.message}
                            </p>

                            <div className="flex gap-3 w-full">
                                <button
                                    onClick={() => setConfirmModal({ ...confirmModal, show: false })}
                                    className="flex-1 px-4 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmModal.onConfirm}
                                    className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 text-white font-medium hover:bg-red-600 shadow-lg shadow-red-500/30 transition-colors"
                                >
                                    Yes, Remove
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPopup;
